import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {Component<T>}
 */
export class ProductCardLink extends Component {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    if (event.target instanceof Element) {
      const interactiveElement = event.target.closest(
        'button, input, label, select, [tabindex="1"], .color-swatch-js, [class*="wishlist"]'
      );
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false'
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);
    this.addEventListener('keydown', this.#onColorSwatchKeydown);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener('keydown', this.#onColorSwatchKeydown);
  }

  #onColorSwatchKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (!(event.target instanceof Element)) return;
    const swatch = event.target.closest('.color-swatch-js');
    if (!swatch || !this.contains(swatch)) return;
    if (this.#colorSwatchWrappedInProductLink(swatch)) return;
    event.preventDefault();
    this.#applyColorSwatchToGallery(swatch);
  };

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const responseProductCard = event.detail.data.html?.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    const activeSwatch = this.#getActiveColorSwatch();
    const hoverUrl = activeSwatch?.getAttribute('data-related-image2');
    const { secondary } = this.#getGalleryPreviewSlides();

    if (activeSwatch && hoverUrl && secondary) {
      const alt = activeSwatch.getAttribute('data-related-alt') || '';
      this.#setSlideImageFromUrl(secondary, hoverUrl, alt);
      slideshow.select(1, undefined, { animate: false });
      return;
    }

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Previews a linked color swatch on hover.
   * @param {PointerEvent} event - The pointer event.
   */
  previewColorSwatch(event) {
    if (event.pointerType !== 'mouse') return;
    if (!(event.target instanceof Element)) return;

    const swatch = event.target.closest('.color-swatch-js');
    if (!swatch || !this.contains(swatch)) return;

    this.resetVariant.cancel();
    this.#updateGalleryFromColorSwatch(swatch, 0);
  }

  /**
   * Resets gallery preview after linked color swatch hover.
   * @param {PointerEvent} event - The pointer event.
   */
  resetColorSwatch(event) {
    if (event.pointerType !== 'mouse') return;
    this.resetVariant(event);
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    if (this.#getActiveColorSwatch()) {
      this.#resetVariant();
      return;
    }

    if (!this.variantPicker) {
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    const selectedSwatch = this.#getActiveColorSwatch();
    if (selectedSwatch) {
      this.#updateGalleryFromColorSwatch(selectedSwatch, 0);
      return;
    }

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * @returns {Element | null}
   */
  #getActiveColorSwatch() {
    return this.querySelector('.product-block-options__item--selected.color-swatch-js');
  }

  /**
   * Visible gallery slides used for primary/hover image swaps (skips hidden variant slides).
   * @returns {{ slideshow: import('slideshow').Slideshow | null, primary: Element | null, secondary: Element | null }}
   */
  #getGalleryPreviewSlides() {
    const slideshow = this.refs.slideshow;
    const allSlides = slideshow?.refs?.slides;

    if (!slideshow || !allSlides?.length) {
      return { slideshow: null, primary: null, secondary: null };
    }

    const visible = allSlides.filter(
      (slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal')
    );

    return {
      slideshow,
      primary: visible[0] ?? null,
      secondary: visible[1] ?? null,
    };
  }

  /**
   * Updates visible gallery slide images from a linked color swatch.
   * @param {Element} swatch
   * @param {number} [slideIndex]
   * @returns {boolean}
   */
  #updateGalleryFromColorSwatch(swatch, slideIndex = 0) {
    const url1 = swatch.getAttribute('data-related-image');
    const url2 = swatch.getAttribute('data-related-image2');
    const alt = swatch.getAttribute('data-related-alt') || '';
    const { slideshow, primary, secondary } = this.#getGalleryPreviewSlides();

    if (!slideshow || !primary) {
      if (this.productTransitionEnabled && url1) {
        this.setAttribute('data-featured-media-url', url1);
      }
      return false;
    }

    this.#setSlideImageFromUrl(primary, url1, alt);
    if (secondary && url2) {
      this.#setSlideImageFromUrl(secondary, url2, alt);
    }

    slideshow.select(slideIndex, undefined, { animate: false });

    if (this.productTransitionEnabled && url1) {
      this.setAttribute('data-featured-media-url', url1);
    }

    return true;
  }

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const swatch = event.target.closest('.color-swatch-js');
    if (swatch && this.contains(swatch)) {
      if (this.#colorSwatchWrappedInProductLink(swatch)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.#applyColorSwatchToGallery(swatch);
      return;
    }

    // The wishlist app renders divs and spans, so its widget would otherwise fall
    // through this guard and the card would navigate while the heart was being
    // clicked. The match is on any wishlist class rather than the container
    // alone: the app re-renders the widget on click, so by the time this handler
    // runs the clicked node is already detached and its chain no longer reaches
    // .wishlist-engine.
    const interactiveElement = event.target.closest(
      'button, input, label, select, [tabindex="1"], [class*="wishlist"]'
    );

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Color swatches wrapped in `<a href>` (block setting "Product") use native navigation.
   * @param {Element} swatch
   * @returns {boolean}
   */
  #colorSwatchWrappedInProductLink(swatch) {
    const a = swatch.closest('a[href]');
    return a instanceof HTMLAnchorElement && a !== this.refs.productCardLink;
  }

  /**
   * Updates card gallery images from collection color swatch data attributes and shows first slide.
   * @param {Element} swatch
   */
  #applyColorSwatchToGallery(swatch) {
    const row = swatch.closest('.product-block-options--color-swatches');
    if (row) {
      row.querySelectorAll('.product-block-options__item--selected').forEach((el) =>
        el.classList.remove('product-block-options__item--selected')
      );
      row.querySelectorAll('.product-block-options__item--current').forEach((el) =>
        el.classList.remove('product-block-options__item--current')
      );
      row.querySelectorAll('[data-current="true"]').forEach((el) => el.removeAttribute('data-current'));
      row.querySelectorAll('[aria-current="true"]').forEach((el) => el.removeAttribute('aria-current'));
      swatch.classList.add('product-block-options__item--selected', 'product-block-options__item--current');
      swatch.setAttribute('data-current', 'true');
      swatch.setAttribute('aria-current', 'true');
    }

    const variantId = swatch.getAttribute('data-related-variant-id');
    const variantAvailable = swatch.getAttribute('data-related-variant-available');
    const relatedUrl = swatch.getAttribute('data-related-url');
    const relatedProductId = swatch.getAttribute('data-related-product-id');
    const relatedTitle = swatch.getAttribute('data-related-product-title');

    /** @type {string | null} */
    let productHref = null;
    if (relatedUrl) {
      try {
        const u = new URL(relatedUrl, window.location.origin);
        if (variantId) u.searchParams.set('variant', variantId);
        productHref = u.href;
      } catch {
        productHref = null;
      }
    }

    const variantInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('quick-add-component input[name="id"]')
    );
    if (variantInput && variantId) {
      variantInput.value = variantId;
      variantInput.toggleAttribute('disabled', variantAvailable === 'false');
    }

    const productForm = /** @type {HTMLElement | null} */ (
      this.querySelector('quick-add-component product-form-component')
    );
    if (productForm && relatedProductId) {
      productForm.setAttribute('data-product-id', relatedProductId);
    }

    const quickAdd = this.refs.quickAdd;
    if (quickAdd instanceof HTMLElement && relatedTitle) {
      quickAdd.setAttribute('data-product-title', relatedTitle);
    }

    const addToCartBtn = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('quick-add-component button.quick-add__button--add[type="submit"]')
    );
    if (addToCartBtn && variantAvailable != null) {
      addToCartBtn.disabled = variantAvailable === 'false';
    }

    if (productHref) {
      const { productCardLink, cardGalleryLink } = this.refs;
      if (productCardLink instanceof HTMLAnchorElement) productCardLink.href = productHref;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productHref;
        if (relatedTitle) cardGalleryLink.setAttribute('aria-label', relatedTitle);
      }
      void this.refs.quickAdd?.fetchProductPage?.(productHref);
      void this.#syncCardDataFromProductHref(productHref);
    }

    if (relatedTitle) {
      const titleLink = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[ref="productTitleLink"]'));
      if (titleLink instanceof HTMLAnchorElement) {
        if (productHref) titleLink.href = productHref;
        titleLink.setAttribute('aria-label', relatedTitle);
        const heading =
          titleLink.querySelector('p[role="heading"]') || titleLink.querySelector('.title-text') || titleLink.querySelector('p');
        if (heading) heading.textContent = relatedTitle;
      }

      const cardSrTitle = this.refs.productCardLink?.querySelector('.visually-hidden');
      if (cardSrTitle) cardSrTitle.textContent = relatedTitle;

      const zoomTitle = this.querySelector('.product-grid-view-zoom-out--details h3');
      if (zoomTitle) zoomTitle.textContent = relatedTitle;
    }

    if (!this.#updateGalleryFromColorSwatch(swatch, 0)) {
      return;
    }

    if (this.productTransitionEnabled && swatch.getAttribute('data-related-image')) {
      this.setAttribute('data-featured-media-url', swatch.getAttribute('data-related-image'));
    }
  }

  /**
   * @param {Element | undefined} slide
   * @param {string | null} url
   * @param {string} alt
   */
  #setSlideImageFromUrl(slide, url, alt) {
    if (!slide || !url) return;

    slide.querySelectorAll('img.product-media__image, img.deferred-media__poster-image').forEach((img) => {
      if (!(img instanceof HTMLImageElement)) return;
      img.src = url;
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      if (alt) img.alt = alt;
    });
  }

  /**
   * Fetch selected product-card section and sync dynamic card data (price + quick-add media).
   * @param {string} productHref
   */
  async #syncCardDataFromProductHref(productHref) {
    let requestUrl;
    try {
      const u = new URL(productHref, window.location.origin);
      u.searchParams.set('section_id', 'section-rendering-product-card');
      requestUrl = u.toString();
    } catch {
      return;
    }

    try {
      const response = await fetch(requestUrl);
      if (!response.ok) return;
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const incomingCard = html.querySelector('product-card');
      if (!incomingCard) return;

      const currentPrice = this.querySelector(`product-price [ref='priceContainer']`);
      const incomingPrice = incomingCard.querySelector(`product-price [ref='priceContainer']`);
      if (currentPrice && incomingPrice) {
        morph(currentPrice, incomingPrice);
        this.#stripCurrencyCodeFromPriceContainer(currentPrice);
      }

      const currentAddToCart = this.querySelector('quick-add-component add-to-cart-component');
      const incomingAddToCart = incomingCard.querySelector('quick-add-component add-to-cart-component');
      const incomingMedia = incomingAddToCart?.getAttribute('data-product-variant-media');
      if (currentAddToCart && incomingMedia) {
        currentAddToCart.setAttribute('data-product-variant-media', incomingMedia);
      }
    } catch {
      // ignore sync failures; swatch switch still updates title/image/url/variant id
    }
  }

  /**
   * Remove ISO currency suffixes (e.g. "USD") from rendered card prices.
   * Keeps amount and symbol only.
   * @param {Element} container
   */
  #stripCurrencyCodeFromPriceContainer(container) {
    const ISO_CURRENCY_SUFFIX = /\s+[A-Z]{3}\b/g;
    container.querySelectorAll('.money, .price, .compare-at-price, .price-item--sale, .price-item--regular').forEach((el) => {
      if (el.childElementCount > 0) return;
      if (!el.textContent) return;
      el.textContent = el.textContent.replace(ISO_CURRENCY_SUFFIX, '').trim();
    });
  }

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
